<template>
  <div>
    <div ref="quillContainer" />
    <InputError :message="error" class="mt-2" />
  </div>
</template>

<script setup>
import { ref, watch, onMounted, onBeforeUnmount } from "vue";
import InputError from "@/Components/InputError.vue";
import Quill from "quill";
import "quill/dist/quill.snow.css";
import QuillResizeImage from "quill-resize-image";
import Swal from "sweetalert2";

// Daftarkan modul resize gambar sekali saja (global pada Quill).
if (!Quill.imports["modules/resizeImage"]) {
  Quill.register("modules/resizeImage", QuillResizeImage);
}

// Batas unggah gambar (selaras dengan validasi server: max:2048 KB).
const MAX_IMAGE_SIZE = 2 * 1024 * 1024; // 2 MB
const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png"];

const props = defineProps({
  modelValue: String,
  error: String,
});
const emit = defineEmits(["update:modelValue"]);

const quillContainer = ref(null);
let quill = null;

function imageHandler() {
  const input = document.createElement("input");
  input.setAttribute("type", "file");
  input.setAttribute("accept", "image/jpeg,image/jpg,image/png");
  input.click();
  input.onchange = async () => {
    const file = input.files?.[0];
    if (!file) return;

    // Validasi tipe berkas
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      Swal.fire({
        icon: "warning",
        title: "Format tidak didukung",
        text: "Gambar harus berformat JPG, JPEG, atau PNG.",
      });
      return;
    }

    // Validasi ukuran berkas (batas 2 MB)
    if (file.size > MAX_IMAGE_SIZE) {
      Swal.fire({
        icon: "warning",
        title: "Ukuran gambar terlalu besar",
        text: `Maksimal 2 MB. Ukuran berkas Anda ${(
          file.size /
          1024 /
          1024
        ).toFixed(2)} MB.`,
      });
      return;
    }

    const formData = new FormData();
    formData.append("image", file);
    try {
      const csrf = document
        .querySelector('meta[name="csrf-token"]')
        ?.getAttribute("content");
      const response = await fetch("/berita/upload-image", {
        method: "POST",
        body: formData,
        headers: {
          Accept: "application/json",
          ...(csrf ? { "X-CSRF-TOKEN": csrf } : {}),
        },
      });

      if (!response.ok) {
        let msg = "Gagal mengunggah gambar.";
        if (response.status === 422) {
          const err = await response.json().catch(() => null);
          msg =
            err?.errors?.image?.[0] ||
            err?.message ||
            "Gambar melebihi batas yang diizinkan (maks 2 MB).";
        } else if (response.status === 413) {
          msg = "Gambar melebihi batas ukuran unggah server.";
        }
        Swal.fire({ icon: "error", title: "Gagal", text: msg });
        return;
      }

      const data = await response.json();
      const url = data.url || data.path || data.location;
      if (url) {
        const range = quill.getSelection(true) || { index: quill.getLength() };
        quill.insertEmbed(range.index, "image", url);
        quill.setSelection(range.index + 1);
      } else {
        Swal.fire({
          icon: "error",
          title: "Gagal",
          text: "URL gambar tidak ditemukan pada respons server.",
        });
      }
    } catch (e) {
      Swal.fire({
        icon: "error",
        title: "Gagal",
        text: "Terjadi kesalahan saat mengunggah gambar.",
      });
    }
  };
}

onMounted(() => {
  quill = new Quill(quillContainer.value, {
    theme: "snow",
    modules: {
      toolbar: {
        container: [
          ["bold", "italic", "underline", "strike"],
          ["blockquote", "code-block"],
          [{ header: 1 }, { header: 2 }],
          [{ list: "ordered" }, { list: "bullet" }],
          [{ script: "sub" }, { script: "super" }],
          [{ indent: "-1" }, { indent: "+1" }],
          [{ direction: "rtl" }],
          [{ size: ["small", false, "large", "huge"] }],
          [{ header: [1, 2, 3, 4, 5, 6, false] }],
          [{ color: [] }, { background: [] }],
          [{ font: [] }],
          [{ align: [] }],
          ["link", "image", "video"],
          ["clean"],
        ],
        handlers: {
          image: imageHandler,
        },
      },
      // Gambar yang disisipkan dapat diubah ukurannya langsung di editor.
      resizeImage: {
        displaySize: true,
      },
    },
    placeholder: "Tulis konten berita di sini...",
  });
  quill.root.innerHTML = props.modelValue || "";
  quill.on("text-change", () => {
    emit("update:modelValue", quill.root.innerHTML);
  });
});

watch(
  () => props.modelValue,
  (val) => {
    // Jangan timpa konten saat user sedang mengetik (mencegah kursor/format
    // list terganggu). Hanya sinkronkan saat perubahan datang dari luar.
    if (quill && !quill.hasFocus() && val !== quill.root.innerHTML) {
      quill.root.innerHTML = val || "";
    }
  }
);

onBeforeUnmount(() => {
  quill = null;
});
</script>

<style scoped>
.ql-container {
  min-height: 500px;
  border: 1px solid #e5e7eb;
  background: #fff;
}

/* Pastikan marker list (bullet/nomor) tetap tampil di editor —
   override reset Tailwind preflight (ul,ol { padding:0; list-style:none }). */
:deep(.ql-editor ol),
:deep(.ql-editor ul) {
  padding-left: 1.5em;
}

:deep(.ql-editor ul > li) {
  list-style-type: none;
}

:deep(.ql-editor ul > li)::before {
  content: "\2022";
}

:deep(.ql-editor li:not(.ql-direction-rtl))::before {
  margin-left: -1.5em;
  margin-right: 0.3em;
  text-align: right;
}
</style>
